import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import type { useRouter } from 'next/router'

import { BaseViewModel } from './BaseViewModel'
import type { PlaybookStore } from './PlaybookStore'
import { URLS } from '@components/playbook/urls'
import { AnalyticsEvent } from '@lib/analytics/events'
import type { SessionStore } from '@lib/store/session/SessionStore'
import { LoginStatus, OtpVerificationStatus } from '@lib/utils/login'

export type VerificationStep =
    | 'input'
    | 'code-input'
    | 'additional-step'
    | 'existing-account'

export abstract class BaseVerificationViewModel extends BaseViewModel {
    // Step management
    currentStep: VerificationStep = 'input'

    // Input values
    inputValue = ''
    verificationCode = ''

    // UI state
    canResendCode = true
    submitCount = 0

    constructor(
        router: ReturnType<typeof useRouter>,
        searchParams: URLSearchParams,
        playbook: PlaybookStore,
        sessionStore: SessionStore,
    ) {
        super(router, searchParams, playbook, sessionStore)

        makeObservable(this, {
            currentStep: observable,
            inputValue: observable,
            verificationCode: observable,
            canResendCode: observable,
            submitCount: observable,
            setStep: action,
            setInputValue: action,
            setVerificationCode: action,
            setCanResendCode: action,
            incrementSubmitCount: action,
            clearInputs: action,
            isInputValid: computed,
            isCodeValid: computed,
            canSubmitInput: computed,
            canSubmitCode: computed,
        })

        // Common initialization logic
        this.updatePlaybookForStep(this.currentStep)

        // Check for verification code in URL params
        const codeParam = this.getQueryParam('code')
        if (codeParam) {
            this.setVerificationCode(codeParam)
        }
    }

    // Computed properties
    get isInputValid(): boolean {
        return this.validateInput(this.inputValue)
    }

    get isCodeValid(): boolean {
        return this.verificationCode.length === 6
    }

    get canSubmitInput(): boolean {
        return this.isInputValid && !this.loading
    }

    get canSubmitCode(): boolean {
        return this.isCodeValid && !this.loading && !this.isSubmitting
    }

    // Abstract helper getters
    abstract get inputValidationError(): string
    abstract get verificationType(): string

    abstract get successRedirectUrl(): string
    abstract get backUrl(): string

    // Actions
    setStep(step: VerificationStep) {
        this.currentStep = step
        this.updatePlaybookForStep(step)
        this.scrollToTop()
    }

    setInputValue(value: string) {
        this.inputValue = this.formatInput(value)
        this.clearError()
    }

    setVerificationCode(code: string) {
        // Format verification code (remove non-digits, limit to 6)
        this.verificationCode = code.replace(/\D/g, '').slice(0, 6)
        this.clearError()
    }

    setCanResendCode(canResend: boolean) {
        this.canResendCode = canResend
    }

    incrementSubmitCount() {
        this.submitCount += 1
    }

    clearInputs() {
        this.verificationCode = ''
        this.clearError()
    }

    // Common verification flow methods
    async handleSendCode(): Promise<void> {
        this.incrementSubmitCount()

        if (!this.isInputValid) {
            this.setError(this.inputValidationError)
            return
        }

        await this.executeWithErrorHandling(async () => {
            await this.sendVerificationCode()
            runInAction(() => {
                this.setStep('code-input')
            })
        }, 'Failed to send verification code')
    }

    async handleVerifyCode(code?: string): Promise<void> {
        const codeToVerify = code || this.verificationCode

        if (codeToVerify.length !== 6) {
            this.setError('Please enter the 6-digit verification code')
            return
        }

        await this.executeWithErrorHandling(
            async () => {
                const result = await this.verifyCode(codeToVerify)
                await this.handleVerificationResult(result)
            },
            'Verification failed',
            'submitting',
        )
    }

    async handleResendCode() {
        if (!this.canResendCode) {
            this.setError('Cannot resend code at this time')
            return
        }

        await this.executeWithErrorHandling(async () => {
            this.clearInputs()
            await this.resendVerificationCode()
        }, 'Failed to resend code')
    }

    async handleBack() {
        if (this.currentStep === 'code-input') {
            this.setStep('input')
            this.clearInputs()
        } else {
            await this.navigate(this.backUrl)
        }
    }

    async handleKeyPress(e: React.KeyboardEvent) {
        if (e.key === 'Enter') {
            if (this.currentStep === 'input') {
                await this.handleSendCode()
            } else if (this.currentStep === 'code-input') {
                await this.handleVerifyCode()
            }
        }
    }

    // Optional cleanup
    dispose(): void {
        // Cleanup any subscriptions or timers if needed
    }

    // Protected helper methods
    protected async handleVerificationResult(
        status: OtpVerificationStatus,
    ): Promise<void> {
        switch (status) {
            case OtpVerificationStatus.SUCCESS:
                await this.handleVerificationSuccess()
                break

            case OtpVerificationStatus.FAIL:
                this.setError('Invalid verification code. Please try again.')
                break

            case OtpVerificationStatus.NO_SESSION:
                this.setError(
                    'Verification session expired. Please start over.',
                )
                this.setStep('input')
                break

            case OtpVerificationStatus.ERROR:
                this.setError('Verification failed. Please try again.')
                break

            default:
                this.setError('An unexpected error occurred')
        }
    }

    protected async handleVerificationSuccess(): Promise<void> {
        const loginResult = await this.sessionStore.login()

        if (loginResult === LoginStatus.UNSUPPORTED_FACTOR) {
            await this.navigate(URLS.UNSUPPORTED_AUTH_METHOD)
            return
        }

        // Track successful verification
        await this.trackEvent(AnalyticsEvent.VERIFICATION_SUCCESSFUL, {
            type: this.verificationType,
            inputValue: this.inputValue,
        })

        await this.navigate(this.successRedirectUrl)
    }

    protected updatePlaybookForStep(step: VerificationStep): void {
        switch (step) {
            case 'input':
                this.setHeaderText('')
                this.setHeaderBackButtonVisible(true)
                this.setBackButtonFunction(async () => {
                    await this.navigate(this.backUrl)
                })
                break

            case 'code-input':
                this.setHeaderText('')
                this.setHeaderBackButtonVisible(true)
                this.setBackButtonFunction(async () => {
                    await this.handleBack()
                })
                break
        }
    }

    protected scrollToTop(): void {
        window.scrollTo(0, 0)
    }

    // Abstract methods to be implemented by subclasses
    protected abstract validateInput(value: string): boolean
    protected abstract formatInput(value: string): string
    protected abstract sendVerificationCode(): Promise<void>
    protected abstract verifyCode(code: string): Promise<OtpVerificationStatus>
    protected abstract resendVerificationCode(): Promise<void>
}
